import { useFirestoreCollection } from "@p0security/frontend-core/react";
import { PermissionRequest } from "@p0security/shared/types/permission";
import { RequestStatus } from "@p0security/shared/types/request-status";
import { Tenant } from "components/Login";
import { QueryConstraint, limit, orderBy, where } from "firebase/firestore";
import { PERMISSION_REQUESTS_COLLECTION } from "firestore/constants";
import { useContext, useEffect, useMemo, useState } from "react";

export const useRequests = (
  /** Possible statuses to retrieve from the document store */
  statuses: readonly RequestStatus[],
  options?: {
    /** The maximum number of requests to return from the document store */
    limit?: number;
    /** The earliest date after which to query for requests */
    since?: number;
  }
) => {
  const [loading, setLoading] = useState(true);
  const tenantId = useContext(Tenant);

  const queryConstraints: QueryConstraint[] = useMemo(() => {
    const constraints: QueryConstraint[] = [
      where("status", "in", statuses),
      orderBy("requestedTimestamp", "desc"),
    ];

    if (options?.since) {
      constraints.push(where("requestedTimestamp", ">=", options.since));
    }
    if (options?.limit) {
      constraints.push(limit(options.limit));
    }

    return constraints;
  }, [statuses, options?.since, options?.limit]);

  const { docs: requests } = useFirestoreCollection<PermissionRequest>(
    `o/${tenantId}/${PERMISSION_REQUESTS_COLLECTION}`,
    {
      live: true,
      queryConstraints,
    }
  );

  useEffect(() => {
    if (requests !== undefined) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [requests]);

  return {
    loading,
    requests: requests ?? [],
    hasRequests: !loading && !!requests?.length,
  };
};
